import React from 'react';
import { TOLERANCE_UNITS } from '../../utils/shippingTolerance';

// Paired lower/upper tolerance control — the trade recap's control, extracted.
//
// Renders exactly the markup TradeRecap.js renders inline for
// ``sale_shipping_tolerance`` / ``purchase_shipping_tolerance``
// (.tolerance-inputs-container > two .tolerance-input-group cells, each a
// guarded numeric input plus a unit <select>), so the freight recap gets the
// same control rather than a fourth hand-rolled copy.
//
// Contract:
//   - ``values``: { lowerValue, lowerUnit, upperValue, upperUnit }.
//   - ``onChange(patch)``: fired with just the changed key(s), e.g.
//     { lowerValue: '10' } — the caller merges it into its own form state.
//   - ``namePrefix``: field-name/id stem, so the inputs carry stable names
//     (``<prefix>_lower_value`` …) for autofill and tests.
//
// A blank bound stays blank: this control never seeds a 0.
const NUMERIC = /^\d*\.?\d*$/;

const ToleranceInput = ({
  values = {},
  onChange,
  namePrefix = 'tolerance',
  units = TOLERANCE_UNITS,
  disabled = false,
  onInvalid,
}) => {
  const emit = (patch) => {
    if (typeof onChange === 'function') onChange(patch);
  };

  const handleValue = (key, raw) => {
    if (!NUMERIC.test(raw)) {
      if (typeof onInvalid === 'function') onInvalid(raw);
      return;
    }
    emit({ [key]: raw });
  };

  const bound = (side, valueKey, unitKey, label) => (
    <div className="tolerance-input-group">
      <div className="tolerance-input-with-dropdown">
        <input
          type="text"
          id={`${namePrefix}_${side}_value`}
          name={`${namePrefix}_${side}_value`}
          aria-label={`${label} tolerance value`}
          value={values[valueKey] || ''}
          onChange={(e) => handleValue(valueKey, e.target.value)}
          placeholder="0"
          inputMode="decimal"
          disabled={disabled}
        />
        <select
          id={`${namePrefix}_${side}_unit`}
          name={`${namePrefix}_${side}_unit`}
          aria-label={`${label} tolerance unit`}
          value={values[unitKey] || '%'}
          onChange={(e) => emit({ [unitKey]: e.target.value })}
          className="tolerance-unit-dropdown"
          disabled={disabled}
        >
          {units.map((unit) => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </select>
      </div>
      <small className="field-description">{label}</small>
    </div>
  );

  return (
    <div className="tolerance-inputs-container">
      {bound('lower', 'lowerValue', 'lowerUnit', 'Lower')}
      {bound('upper', 'upperValue', 'upperUnit', 'Upper')}
    </div>
  );
};

export default ToleranceInput;
